// Performance optimization utilities for Bulgarian market

// Preload critical fonts
export const preloadFonts = () => {
  const fonts = [
    '/fonts/main-font.woff2',
    '/fonts/cyrillic-font.woff2' // Important for Bulgarian text
  ];
  
  fonts.forEach(font => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.href = font;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// Optimize for slow connections (common in some Bulgarian regions)
export const detectConnectionSpeed = (): 'slow' | 'fast' => {
  const connection = (navigator as any).connection || 
                    (navigator as any).mozConnection || 
                    (navigator as any).webkitConnection;
  
  if (connection) {
    // Check for slow connection types
    if (connection.effectiveType === '2g' || 
        connection.effectiveType === 'slow-2g' ||
        connection.saveData === true) {
      return 'slow';
    }
  }
  
  return 'fast';
};

// Lazy load non-critical CSS
export const lazyLoadCSS = (href: string) => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.media = 'print';
  link.onload = function() {
    (this as any).media = 'all';
  };
  document.head.appendChild(link);
};

// Prefetch important links
export const prefetchLinks = () => {
  const importantLinks = [
    '/#services',
    '/#pricing',
    '/#contact'
  ];
  
  importantLinks.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  });
};

// Optimize images for retina displays
export const getOptimizedImageSrc = (src: string, isRetina: boolean = false): string => {
  if (isRetina && window.devicePixelRatio > 1) {
    // Return 2x version for retina displays
    return src.replace(/\.(jpg|png|webp)$/, '@2x.$1');
  }
  return src;
};

// Web Vitals tracking
export const reportWebVitals = (metric: any) => {
  // Send to analytics
  if (window.gtag) {
    window.gtag('event', metric.name, {
      value: Math.round(metric.value),
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
    });
  }
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(metric);
  }
};

// Initialize performance optimizations
export const initPerformanceOptimizations = () => {
  // Preload fonts
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preloadFonts);
  } else {
    preloadFonts();
  }
  
  // Prefetch important links after initial load
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(prefetchLinks);
  } else {
    setTimeout(prefetchLinks, 2000);
  }
  
  // Detect connection speed and adjust accordingly
  const connectionSpeed = detectConnectionSpeed();
  if (connectionSpeed === 'slow') {
    // Reduce animations and load lower quality images for slow connections
    document.documentElement.classList.add('reduce-motion');
    document.documentElement.setAttribute('data-connection', 'slow');
  }
};

// Debounce function for scroll/resize events
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function for frequent events
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Optimize animations for performance
export const optimizeAnimations = () => {
  // Pause animations when page is not visible
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      document.body.classList.add('pause-animations');
    } else {
      document.body.classList.remove('pause-animations');
    }
  });
  
  // Use CSS containment for better performance
  const sections = document.querySelectorAll('section, .container');
  sections.forEach(section => {
    (section as HTMLElement).style.contain = 'layout style paint';
  });
};

declare global {
  interface Window {
    gtag: any;
  }
}
